import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/api';
import { useBreadcrumbTitle } from '../hooks/useBreadcrumb';
import {
  App,
  Card,
  Descriptions,
  Button,
  Space,
  Typography,
  Tag,
  Spin,
  Row,
  Col,
  Statistic,
  message,
  Tabs,
  Dropdown,
  Form,
  Select,
  DatePicker,
  Alert,
  Table,
  Tooltip,
} from 'antd';
import { NumericInput, RichTextEditor, MarkdownDisplay, withDevModalFilePath } from '../components/common';
import BalancedDescriptions from '../components/common/BalancedDescriptions';
import type { MenuProps } from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  BarChartOutlined,
  MoreOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  ShoppingCartOutlined,
  CopyOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { PermissionButton } from '../components/common/PermissionButton';
import { usePermission } from '../hooks/usePermission';
import dayjs from 'dayjs';
import { PartFormModal } from '../components/parts/PartFormModal';
import { StockAdjustmentModal } from '../components/parts/StockAdjustmentModal';
import { StockHistoryChart } from '../components/parts/StockHistoryChart';
import { CategoryTag } from '../components/common/CategoryTag';
import { NameLink } from '../components/common';
import { LocationDisplay } from '../components/common/LocationDisplay';
import { DocumentListSection } from '../components/document/DocumentListSection';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/PartDetailPage.tsx');
const TAX_INCLUDED_UNIT_PRICE_HELP = '税込金額を入力してください。発注書ではこの単価に消費税を追加しません。';
const actionButtonStyle: CSSProperties = {
  height: 40,
  paddingInline: 16,
  borderRadius: 6,
};

interface RelatedPurchaseOrderRow {
  order: {
    id: string;
    displayId?: string | null;
    orderDate?: string | null;
    status: string;
  };
  supplier?: {
    id: string;
    displayId: string;
    name?: string | null;
  } | null;
  partItem?: {
    quantity?: number | null;
    receivedQuantity?: number | null;
  } | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeSupplierSummary = (value: unknown) => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }
  return {
    id: value.id,
    name: value.name,
    phone: typeof value.phone === 'string' ? value.phone : null,
    email: typeof value.email === 'string' ? value.email : null,
  };
};

const normalizeLocationSummary = (value: unknown) => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }
  return {
    id: value.id,
    name: value.name,
    path: typeof value.path === 'string' ? value.path : null,
    branch:
      isRecord(value.branch) && typeof value.branch.id === 'string' && typeof value.branch.name === 'string'
        ? { id: value.branch.id, name: value.branch.name }
        : null,
  };
};

const normalizeRelatedPurchaseOrderRow = (value: unknown): RelatedPurchaseOrderRow | null => {
  if (!isRecord(value) || !isRecord(value.order) || typeof value.order.id !== 'string') {
    return null;
  }

  return {
    order: {
      id: value.order.id,
      displayId: typeof value.order.displayId === 'string' ? value.order.displayId : null,
      orderDate: typeof value.order.orderDate === 'string' ? value.order.orderDate : null,
      status: typeof value.order.status === 'string' ? value.order.status : 'draft',
    },
    supplier:
      isRecord(value.supplier) && typeof value.supplier.id === 'string' && typeof value.supplier.name === 'string'
        ? {
            id: value.supplier.id,
            displayId: typeof value.supplier.displayId === 'string' ? value.supplier.displayId : '',
            name: value.supplier.name,
          }
        : null,
    partItem:
      isRecord(value.partItem)
        ? {
            quantity: typeof value.partItem.quantity === 'number' ? value.partItem.quantity : null,
            receivedQuantity:
              typeof value.partItem.receivedQuantity === 'number' ? value.partItem.receivedQuantity : null,
          }
        : null,
  };
};

export function PartDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { canEdit, canManage, getDisabledMessage } = usePermission();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAdjustModalVisible, setIsAdjustModalVisible] = useState(false);
  const [isConsumeModalVisible, setIsConsumeModalVisible] = useState(false);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { modal } = App.useApp();
  const [consumeForm] = Form.useForm();
  const [orderForm] = Form.useForm();

  // 部品・消耗品詳細取得
  const { data: part, isLoading, refetch } = trpc.parts.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  // パンくずリストに部品名を設定
  useBreadcrumbTitle('part', part?.name);

  const { data: stockLogsData } = trpc.partStockLogs.list.useQuery({
    partId: id!,
    page: 1,
    limit: 10,
  }, { enabled: !!id });

  const { data: consumptionLogsData } = trpc.partConsumptionLogs.list.useQuery({
    partId: id!,
    page: 1,
    limit: 10,
  }, { enabled: !!id });

  // 関連発注を取得
  const { data: relatedOrders } = trpc.purchaseOrders.getByPartId.useQuery({
    partId: id!,
  }, { enabled: !!id });

  // 設備一覧取得（消費モーダル用）
  const { data: equipmentsData } = trpc.equipment.list.useQuery({
    page: 1,
    limit: 1000,
    filter: { isActive: true },
  }, { enabled: isConsumeModalVisible });

  const deleteMutation = trpc.parts.delete.useMutation({
    onSuccess: async (_data, variables) => {
      messageApi.success('部品・消耗品を削除しました');
      const input = isRecord(variables) ? variables : null;

      // 一覧・詳細のキャッシュを更新して、削除後に一覧へ戻った際に古いデータが残らないようにする
      await Promise.all([
        utils.parts.list.invalidate(),
        typeof input?.id === 'string' ? utils.parts.getById.invalidate({ id: input.id }) : Promise.resolve(),
      ]);

      navigate('/app/parts');
    },
    onError: (error) => {
      messageApi.error(error?.message ?? '部品・消耗品の削除に失敗しました');
    },
  });

  // 消費mutation
  const consumeMutation = trpc.partConsumptionLogs.create.useMutation({
    onSuccess: () => {
      messageApi.success('部品・消耗品を消費しました');
      setIsConsumeModalVisible(false);
      consumeForm.resetFields();
      refetch();
    },
    onError: (error) => {
      messageApi.error(`消費に失敗しました: ${error.message}`);
    },
  });

  // 発注作成mutation
  const createOrderMutation = trpc.purchaseOrders.create.useMutation({
    onSuccess: (data) => {
      messageApi.success('発注を作成しました');
      setIsOrderModalVisible(false);
      orderForm.resetFields();
      // 発注詳細ページにリダイレクト
      navigate(`/app/purchase-orders/${data.order.id}`);
    },
    onError: (error) => {
      messageApi.error(`発注作成に失敗しました: ${error.message}`);
    },
  });

  // 複製mutation
  const duplicateMutation = trpc.parts.duplicate.useMutation({
    onSuccess: (data) => {
      messageApi.success(`部品・消耗品を複製しました: ${data.name}`);
      // 複製した部品・消耗品の詳細ページへ遷移
      navigate(`/app/parts/${data.id}`);
    },
    onError: (error) => {
      messageApi.error(`複製に失敗しました: ${error.message}`);
    },
  });

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!part) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text>部品・消耗品が見つかりませんでした。</Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate('/app/parts')}>
              部品・消耗品一覧に戻る
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const stockLogs = stockLogsData?.items || [];
  const consumptionLogs = consumptionLogsData?.items || [];
  const manufacturerSupplier = normalizeSupplierSummary(part?.manufacturerSupplier);
  const supplier = normalizeSupplierSummary((part as Record<string, unknown> | undefined)?.supplier);
  const partLocation = normalizeLocationSummary(part?.location);
  const normalizedRelatedOrders = (relatedOrders ?? []).flatMap((row) => {
    const normalized = normalizeRelatedPurchaseOrderRow(row);
    return normalized ? [normalized] : [];
  });
  const partFormPart = part
    ? {
        id: part.id,
        name: part.name,
        nameKana: part.nameKana,
        partNumber: part.partNumber,
        serialNumber: part.serialNumber,
        specifications: part.specifications,
        manufacturerSupplierId: part.manufacturerSupplierId,
        manufacturerSupplier,
        supplierId: part.supplierId,
        purchaseUrl: part.purchaseUrl,
        unitPrice: part.unitPrice,
        purchaseLotSize: part.purchaseLotSize,
        purchaseOverhead: part.purchaseOverhead,
        unit: part.unit,
        locationId: part.locationId,
        storageLocation: part.storageLocation,
        optimalStockLevel: part.optimalStockLevel,
        category: part.category,
        managerId: part.managerId,
        notes: part.notes,
      }
    : null;

  const currentStock = part.currentStockLevel ?? 0;
  const optimalStock = part.optimalStockLevel ?? 0;

  // 関連発注のカラム定義
  const purchaseOrderColumns: ColumnsType<RelatedPurchaseOrderRow> = [
    {
      title: '発注番号',
      dataIndex: ['order', 'displayId'],
      key: 'displayId',
      render: (text: string | null | undefined, record: RelatedPurchaseOrderRow) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/purchase-orders/${record.order.id}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: '業者',
      dataIndex: ['supplier', 'name'],
      key: 'supplierName',
    },
    {
      title: '発注数',
      dataIndex: ['partItem', 'quantity'],
      key: 'quantity',
      render: (quantity?: number | null) => quantity ? `${quantity} ${part.unit ?? '個'}` : '-',
    },
    {
      title: '受領済',
      dataIndex: ['partItem', 'receivedQuantity'],
      key: 'receivedQuantity',
      render: (received?: number | null) => received ? `${received} ${part.unit ?? '個'}` : '0',
    },
    {
      title: '発注日',
      dataIndex: ['order', 'orderDate'],
      key: 'orderDate',
      render: (date?: string | null) => date ? new Date(date).toLocaleDateString('ja-JP') : '-',
    },
    {
      title: 'ステータス',
      dataIndex: ['order', 'status'],
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          draft: { label: '下書き', color: 'default' },
          ordered: { label: '発注済', color: 'blue' },
          partial: { label: '一部納品', color: 'orange' },
          delivered: { label: '納品済', color: 'green' },
          cancelled: { label: 'キャンセル', color: 'red' },
        };
        const statusInfo = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
  ];

  const getStockStatusTag = () => {
    if (currentStock === 0) {
      return <Tag icon={<ExclamationCircleOutlined />} color="error" style={{ fontSize: 16, padding: '4px 12px' }}>在庫切れ</Tag>;
    }
    if (currentStock < optimalStock) {
      return <Tag icon={<WarningOutlined />} color="warning" style={{ fontSize: 16, padding: '4px 12px' }}>在庫不足</Tag>;
    }
    return <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 16, padding: '4px 12px' }}>在庫充足</Tag>;
  };

  const handleDelete = () => {
    if (!part?.id) {
      return;
    }

    const targetName = part.name || '部品・消耗品';
    modal.confirm({
      title: '部品・消耗品の削除',
      content: `${targetName} を削除してよろしいですか？`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      centered: true,
      onOk: () => deleteMutation.mutateAsync({ id: part.id }),
    });
  };

  const actionMenuItems: MenuProps['items'] = [
    {
      key: 'duplicate',
      label: canEdit ? '複製' : (
        <Tooltip title={getDisabledMessage('edit')}>
          <span>複製</span>
        </Tooltip>
      ),
      icon: canEdit ? <CopyOutlined /> : <LockOutlined />,
      disabled: duplicateMutation.isPending || !canEdit,
    },
    { type: 'divider' as const },
    {
      key: 'delete',
      label: canManage ? '削除' : (
        <Tooltip title={getDisabledMessage('manage')}>
          <span>削除</span>
        </Tooltip>
      ),
      danger: canManage,
      icon: canManage ? <DeleteOutlined /> : <LockOutlined />,
      disabled: deleteMutation.isPending || !canManage,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      handleDelete();
    } else if (key === 'duplicate') {
      duplicateMutation.mutate({ id: part.id });
    }
  };

  // 消費処理
  const handleConsume = async () => {
    try {
      const values = await consumeForm.validateFields();
      consumeMutation.mutate({
        partId: part!.id,
        quantity: values.quantity,
        equipmentId: values.equipmentId || undefined,
        consumedAt: values.consumedAt?.toDate() || new Date(),
        notes: values.notes,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 発注作成処理
  const handleCreateOrder = async () => {
    try {
      const values = await orderForm.validateFields();
      createOrderMutation.mutate({
        supplierId: values.supplierId,
        orderDate: values.orderDate?.toDate() || new Date(),
        expectedDeliveryDate: values.expectedDeliveryDate?.toDate() || undefined,
        items: [
          {
            partId: part!.id,
            quantity: values.quantity,
            unitPrice: values.unitPrice,
            notes: values.itemNotes,
          },
        ],
        shippingCost: values.shippingCost || 0,
        notes: values.notes,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}

      {/* ヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/parts')}
          >
            部品・消耗品一覧に戻る
          </Button>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>{part.name}</Title>
            </div>
            <Space size={8}>
              <PermissionButton
                icon={<MinusCircleOutlined />}
                onClick={() => {
                  consumeForm.resetFields();
                  consumeForm.setFieldsValue({
                    quantity: 1,
                    consumedAt: dayjs(),
                  });
                  setIsConsumeModalVisible(true);
                }}
                disabled={currentStock === 0}
                style={actionButtonStyle}
                requiredPermission="edit"
              >
                消費
              </PermissionButton>
              <PermissionButton
                icon={<ShoppingCartOutlined />}
                onClick={() => {
                  orderForm.resetFields();
                  orderForm.setFieldsValue({
                    quantity: part.purchaseLotSize || 1,
                    unitPrice: part.unitPrice ? parseFloat(part.unitPrice) : undefined,
                    orderDate: dayjs(),
                  });
                  setIsOrderModalVisible(true);
                }}
                style={actionButtonStyle}
                requiredPermission="edit"
              >
                発注
              </PermissionButton>
              <PermissionButton
                icon={<HistoryOutlined />}
                onClick={() => setIsAdjustModalVisible(true)}
                style={actionButtonStyle}
                requiredPermission="edit"
              >
                棚卸
              </PermissionButton>
              <PermissionButton
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsEditModalVisible(true)}
                style={actionButtonStyle}
                requiredPermission="edit"
              >
                編集
              </PermissionButton>
              <Dropdown
                menu={{ items: actionMenuItems, onClick: handleMenuClick }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="default"
                  icon={<MoreOutlined />}
                  aria-label="その他の操作"
                  loading={deleteMutation.isPending}
                  style={{
                    width: 40,
                    height: 40,
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 6,
                    borderColor: '#d9d9d9',
                  }}
                />
              </Dropdown>
            </Space>
          </Space>
        </Space>
      </div>

      {/* 在庫状況統計 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="現在在庫"
              value={currentStock}
              suffix={part.unit ?? '個'}
              valueStyle={{ color: currentStock === 0 ? '#ff4d4f' : currentStock < optimalStock ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="適正在庫"
              value={optimalStock}
              suffix={part.unit ?? '個'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="在庫充足率"
              value={optimalStock > 0 ? Math.round((currentStock / optimalStock) * 100) : 100}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Space direction="vertical">
              <Text>在庫状況</Text>
              {getStockStatusTag()}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* タブ */}
      <Tabs
        defaultActiveKey="info"
        items={[
          {
            key: 'info',
            label: '基本情報',
            children: (
              <Card>
                <BalancedDescriptions bordered column={2}>
                  <Descriptions.Item label="部品・消耗品名">{part.name}</Descriptions.Item>
                  <Descriptions.Item label="フリガナ">{part.nameKana || '-'}</Descriptions.Item>
                  <Descriptions.Item label="型式">{part.partNumber || '-'}</Descriptions.Item>
                  <Descriptions.Item label="製造番号">{part.serialNumber || '-'}</Descriptions.Item>
                  <Descriptions.Item label="定格">{part.specifications || '-'}</Descriptions.Item>
                  <Descriptions.Item label="メーカー業者">
                    {manufacturerSupplier ? (
                      <Space direction="vertical" size={0}>
                        <NameLink
                          name={manufacturerSupplier.name}
                          onClick={() => navigate(`/app/suppliers/${manufacturerSupplier.id}`)}
                        />
                        {(manufacturerSupplier.phone || manufacturerSupplier.email) && (
                          <Text type="secondary">
                            {[manufacturerSupplier.phone, manufacturerSupplier.email]
                              .filter(Boolean)
                              .join(' / ')}
                          </Text>
                        )}
                      </Space>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="購入先業者">
                    {supplier ? (
                      <Space direction="vertical" size={0}>
                        <NameLink
                          name={supplier.name}
                          onClick={() => navigate(`/app/suppliers/${supplier.id}`)}
                        />
                        {(supplier.phone || supplier.email) && (
                          <Text type="secondary">
                            {[supplier.phone, supplier.email]
                              .filter(Boolean)
                              .join(' / ')}
                          </Text>
                        )}
                      </Space>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="カテゴリ">
                    <CategoryTag category={part.category} />
                  </Descriptions.Item>
                  <Descriptions.Item label="単価(税込)">
                    {part.unitPrice ? `¥${parseFloat(part.unitPrice).toLocaleString()}` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="購入ロット">
                    {part.purchaseLotSize || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="単位">{part.unit ?? '個'}</Descriptions.Item>
                  <Descriptions.Item label="保管エリア">
                    {partLocation ? (
                      <Space direction="vertical" size={0}>
                        <LocationDisplay
                          location={partLocation}
                          showIcon={true}
                          showPath={false}
                        />
                        {partLocation.branch && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            拠点: {partLocation.branch.name}
                          </Text>
                        )}
                      </Space>
                    ) : part.storageLocation ? (
                      <Text>{part.storageLocation}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  {part.storageLocation && part.location && (
                    <Descriptions.Item label="保管場所（補足）">
                      {part.storageLocation}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="適正在庫数">
                    {optimalStock} {part.unit ?? '個'}
                  </Descriptions.Item>
                  <Descriptions.Item label="現在在庫数" span={2}>
                    <Space>
                      <Text strong>{currentStock} {part.unit ?? '個'}</Text>
                      {getStockStatusTag()}
                    </Space>
                  </Descriptions.Item>
                  {part.lastInventoryDate && (
                    <>
                      <Descriptions.Item label="最終棚卸日">
                        {new Date(part.lastInventoryDate).toLocaleDateString('ja-JP')}
                      </Descriptions.Item>
                      <Descriptions.Item label="棚卸在庫数">
                        {part.lastInventoryCount ?? '-'} {part.unit ?? '個'}
                      </Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="詳細" span={2}>
                    {part.notes ? (
                      <MarkdownDisplay content={part.notes} />
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                </BalancedDescriptions>
              </Card>
            ),
          },
          {
            key: 'history',
            label: (
              <Space>
                <BarChartOutlined />
                在庫推移
              </Space>
            ),
            children: (
              <StockHistoryChart partId={part.id} />
            ),
          },
          {
            key: 'stock-logs',
            label: (
              <Space>
                <HistoryOutlined />
                在庫変動履歴
              </Space>
            ),
            children: (
              <Card>
                {stockLogs.length > 0 ? (
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {stockLogs.map((item) => (
                      <Card key={item.log.id} size="small">
                        <Row gutter={16}>
                          <Col span={4}>
                            <Text type="secondary">
                              {new Date(item.log.createdAt).toLocaleString('ja-JP')}
                            </Text>
                          </Col>
                          <Col span={4}>
                            <Tag color={
                              item.log.transactionType === 'in' ? 'success' :
                                item.log.transactionType === 'out' ? 'error' :
                                  'default'
                            }>
                              {item.log.transactionType === 'in' ? '入庫' :
                                item.log.transactionType === 'out' ? '出庫' :
                                  item.log.transactionType === 'adjustment' ? '調整' : '棚卸'}
                            </Tag>
                          </Col>
                          <Col span={4}>
                            <Text strong>
                              {item.log.quantity > 0 ? '+' : ''}{item.log.quantity}
                            </Text>
                          </Col>
                          <Col span={4}>
                            <Text>残高: {item.log.balanceAfter}</Text>
                          </Col>
                          <Col span={8}>
                            <Text type="secondary">{item.log.notes || ''}</Text>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">在庫変動履歴がありません</Text>
                )}
              </Card>
            ),
          },
          {
            key: 'consumption',
            label: (
              <Space>
                <HistoryOutlined />
                消費管理
              </Space>
            ),
            children: (
              <Card>
                {consumptionLogs.length > 0 ? (
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {consumptionLogs.map((item) => (
                      <Card key={item.log.id} size="small">
                        <Row gutter={16}>
                          <Col span={6}>
                            <Text type="secondary">
                              {new Date(item.log.consumedAt).toLocaleString('ja-JP')}
                            </Text>
                          </Col>
                          <Col span={4}>
                            <Text strong>消費数: {item.log.quantity}</Text>
                          </Col>
                          <Col span={14}>
                            <Text type="secondary">{item.log.notes || ''}</Text>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">消費管理がありません</Text>
                )}
              </Card>
            ),
          },
          {
            key: 'orders',
            label: (
              <Space>
                <ShoppingCartOutlined />
                関連発注
              </Space>
            ),
            children: (
              <Card>
                <Table
                  columns={purchaseOrderColumns}
                  dataSource={normalizedRelatedOrders}
                  rowKey={(record) => record.order.id}
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'この部品・消耗品の発注履歴はありません' }}
                />
              </Card>
            ),
          },
          {
            key: 'documents',
            label: '書類',
            children: (
              <DocumentListSection
                entityType="part"
                entityId={part.id}
                allowCreate={true}
              />
            ),
          },
        ]}
      />

      {/* 編集モーダル */}
      <PartFormModal
        open={isEditModalVisible}
        part={partFormPart}
        onClose={() => setIsEditModalVisible(false)}
        onSuccess={() => {
          messageApi.success('部品情報を更新しました');
          refetch();
          setIsEditModalVisible(false);
        }}
      />

      {/* 棚卸モーダル */}
      <StockAdjustmentModal
        open={isAdjustModalVisible}
        part={part}
        onClose={() => setIsAdjustModalVisible(false)}
        onSuccess={() => {
          messageApi.success('在庫を更新しました');
          refetch();
          setIsAdjustModalVisible(false);
        }}
      />

      {/* 消費モーダル */}
      <Modal
        title="部品・消耗品消費"
        open={isConsumeModalVisible}
        onOk={handleConsume}
        onCancel={() => {
          setIsConsumeModalVisible(false);
          consumeForm.resetFields();
        }}
        confirmLoading={consumeMutation.isPending}
        okText="消費"
        cancelText="キャンセル"
      >
        <Form form={consumeForm} layout="vertical">
          <Alert
            message={`現在在庫: ${currentStock} ${part.unit ?? '個'}`}
            type={currentStock === 0 ? 'error' : currentStock < optimalStock ? 'warning' : 'info'}
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            label="消費数量"
            name="quantity"
            rules={[
              { required: true, message: '消費数量を入力してください' },
              { type: 'number', min: 1, message: '1以上の数値を入力してください' },
            ]}
          >
            <NumericInput
              min={1}
              max={currentStock}
              style={{ width: '100%' }}
              addonAfter={part.unit ?? '個'}
              placeholder="消費数量"
            />
          </Form.Item>
          <Form.Item label="設備" name="equipmentId">
            <Select
              placeholder="設備を選択（任意）"
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={equipmentsData?.data?.map((eq) => ({
                label: eq.name,
                value: eq.id,
              })) || []}
            />
          </Form.Item>
          <Form.Item
            label="消費日時"
            name="consumedAt"
            initialValue={dayjs()}
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="詳細"
            name="notes"
            getValueFromEvent={(markdown: string) => markdown}
            getValueProps={(value) => ({ value: value || '' })}
          >
            <RichTextEditor
              placeholder="詳細を入力..."
              minHeight={80}
              showPreviewToggle={false}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 発注モーダル */}
      <Modal
        title="発注作成"
        open={isOrderModalVisible}
        onOk={handleCreateOrder}
        onCancel={() => {
          setIsOrderModalVisible(false);
          orderForm.resetFields();
        }}
        confirmLoading={createOrderMutation.isPending}
        okText="発注"
        cancelText="キャンセル"
        width={600}
      >
        <Form form={orderForm} layout="vertical">
          <Alert
            message={`部品・消耗品: ${part.name}`}
            description={supplier || manufacturerSupplier ? undefined : '業者が設定されていません。部品・消耗品編集で業者を設定してください。'}
            type={supplier || manufacturerSupplier ? 'info' : 'warning'}
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            label="業者"
            name="supplierId"
            rules={[{ required: true, message: '業者を選択してください' }]}
          >
            <Select
              placeholder="業者を選択"
              options={[
                part.supplier && {
                  label: `購入先: ${part.supplier.name}`,
                  value: part.supplier.id,
                },
                manufacturerSupplier && {
                  label: `メーカー: ${manufacturerSupplier.name}`,
                  value: manufacturerSupplier.id,
                },
              ].filter(Boolean) as { label: string; value: string }[]}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="数量"
                name="quantity"
                rules={[{ required: true, message: '数量を入力してください' }]}
              >
                <NumericInput
                  min={1}
                  style={{ width: '100%' }}
                  addonAfter={part.unit ?? '個'}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="単価(税込)"
                name="unitPrice"
                rules={[{ required: true, message: '単価を入力してください' }]}
                extra={TAX_INCLUDED_UNIT_PRICE_HELP}
              >
                <NumericInput
                  min={0}
                  style={{ width: '100%' }}
                  prefix="¥"
                  placeholder="例: 1,200（税込）"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="発注日"
                name="orderDate"
                initialValue={dayjs()}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="納品予定日" name="expectedDeliveryDate">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
                      <Form.Item label="送料(税込)" name="shippingCost">
            <NumericInput
              min={0}
              style={{ width: '100%' }}
              prefix="¥"
              placeholder="0"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </Form.Item>
          <Form.Item
            label="詳細"
            name="notes"
            getValueFromEvent={(markdown: string) => markdown}
            getValueProps={(value) => ({ value: value || '' })}
          >
            <RichTextEditor
              placeholder="詳細を入力..."
              minHeight={80}
              showPreviewToggle={false}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
